import {
  format,
  parse,
  setHours,
  setMilliseconds,
  setMinutes,
  setSeconds,
  startOfDay,
  isSameDay,
} from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";

import styles from "./OrderTimeSelector.module.css";
import { useContext, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import RadioButton from "~/components/RadioButton/RadioButton";
import Button from "~/components/Button/Button";
import ChevronRight from "~/icons/ChevronRight";
import ChevronLeft from "~/icons/ChevronLeft";
import SiteContext from "~/contexts/SiteContext";

interface OrderTimeSelectorProps {
  visible: boolean;
  onSave: (value: Date | null) => void;
  onBack: VoidFunction;
  requestedReadyTime?: string | null;
  options: (string | null)[];
}

const OrderTimeSelector: React.FC<OrderTimeSelectorProps> = ({
  visible,
  onSave,
  onBack,
  requestedReadyTime,
  options: availableTimeSlots,
}) => {
  const [timeSlotsPage, setTimeSlotsPage] = useState(0);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  const { site } = useContext(SiteContext);

  const handleSave = () => {
    if (!selectedTime || !selectedDate) {
      return;
    }

    if (selectedTime === "ASAP") {
      onSave(null);
      return;
    }

    const timeWithAMPM = parse(selectedTime, "hh:mm a", new Date());

    // Combine the selected day (Date object) with the parsed time (hours and minutes)
    const combinedDate = setSeconds(
      setMilliseconds(
        setMinutes(
          setHours(selectedDate, timeWithAMPM.getHours()),
          timeWithAMPM.getMinutes()
        ),
        0
      ),
      0
    );
    // Return UTC formatted date since we only care about site.timeZone for the user facing UI
    const utcDate = fromZonedTime(combinedDate, site.timeZone);
    onSave(utcDate);
  };

  const zonedTimes = useMemo(() => {
    if (!availableTimeSlots) return [];

    // Convert available times to zoned times, preserving null
    return availableTimeSlots.map((time) =>
      time ? toZonedTime(time, site.timeZone) : null
    );
  }, [availableTimeSlots, site.timeZone]);

  const calendarDays = useMemo(() => {
    if (!availableTimeSlots) return [];

    // Get all unique dates from valid time slots
    const validDates = zonedTimes.map((date) => startOfDay(date || new Date()));

    const uniqueDates = Array.from(
      new Set(validDates.map((date) => date.getTime()))
    )
      .map((timestamp) => new Date(timestamp))
      .sort((a, b) => a.getTime() - b.getTime());

    // If no dates available, return empty array
    if (uniqueDates.length === 0) return [];

    return uniqueDates.map((date) => ({
      date,
      dayOfWeek: isSameDay(date, toZonedTime(new Date(), site.timeZone))
        ? "Today"
        : format(date, "eee"),
      dayOfMonth: format(date, "d"),
    }));
  }, [availableTimeSlots, zonedTimes, site.timeZone]);

  const times = useMemo(() => {
    if (!selectedDate || !availableTimeSlots) return [];

    // Filter times for selected date and format them
    const timesForSelectedDate = zonedTimes
      .filter(
        (time): time is Date => time !== null && isSameDay(time, selectedDate)
      )
      .sort((a, b) => a.getTime() - b.getTime())
      .map((time) => format(time, "hh:mm a"));

    // Only include ASAP if this is the first available date AND availableTimeSlots contains null
    const isFirstAvailableDate =
      calendarDays.length > 0 &&
      selectedDate.getTime() === calendarDays[0].date.getTime();
    const hasAsapOption = availableTimeSlots.includes(null);

    return isFirstAvailableDate && hasAsapOption
      ? ["ASAP", ...timesForSelectedDate]
      : timesForSelectedDate;
  }, [selectedDate, availableTimeSlots, zonedTimes, calendarDays]);

  // Set initial date and time based on initialSelectedTime or first available slot
  useEffect(() => {
    if (visible && !selectedDate && calendarDays.length > 0) {
      if (requestedReadyTime) {
        const zonedInitialTime = toZonedTime(requestedReadyTime, site.timeZone);
        const matchingDay = calendarDays.find((day) =>
          isSameDay(day.date, zonedInitialTime)
        );
        if (matchingDay) {
          setSelectedDate(matchingDay.date);
          setSelectedTime(format(zonedInitialTime, "hh:mm a"));
          return;
        }
      }
      // If no initialSelectedTime or matching day not found, select first available day
      setSelectedDate(calendarDays[0].date);
    }
  }, [visible, calendarDays, selectedDate, requestedReadyTime, site.timeZone]);

  // Set initial time when date is selected and no time is selected
  useEffect(() => {
    if (visible && selectedDate && !selectedTime && times.length > 0) {
      if (requestedReadyTime === null && times.includes("ASAP")) {
        setSelectedTime("ASAP");
      } else {
        setSelectedTime(times[0]);
      }
    }
  }, [visible, selectedDate, times, selectedTime, requestedReadyTime]);

  return (
    <div className={styles.content}>
      <div className={styles.sectionTitle}>Choose day and time</div>
      <div className={styles.calendar}>
        <div className={styles.calendarDays}>
          {timeSlotsPage ? (
            <div
              className={styles.arrowButton}
              onClick={() => setTimeSlotsPage((prev) => prev - 1)}
            >
              <ChevronLeft />
            </div>
          ) : (
            <div />
          )}
          {calendarDays
            .slice(timeSlotsPage * 7, timeSlotsPage * 7 + 7)
            .map((day) => (
              <div
                key={day.date.getTime()}
                className={clsx(
                  styles.calendarDay,
                  selectedDate?.getTime() === day.date.getTime() &&
                    styles.selectedCalendarDay
                )}
                onClick={() => setSelectedDate(day.date)}
              >
                <div className={styles.dayOfWeek}>{day.dayOfWeek}</div>
                <div className={styles.dayOfMonth}>{day.dayOfMonth}</div>
              </div>
            ))}
          {(timeSlotsPage + 1) * 7 < calendarDays.length ? (
            <div
              className={styles.arrowButton}
              onClick={() => setTimeSlotsPage((prev) => prev + 1)}
            >
              <ChevronRight />
            </div>
          ) : (
            <div />
          )}
        </div>
        <div className={styles.calendarTimes}>
          {times.map((time) => (
            <div
              key={time}
              className={styles.timeRow}
              onClick={() => setSelectedTime(time)}
            >
              <div>{time}</div>
              <RadioButton checked={selectedTime === time} />
            </div>
          ))}
        </div>
      </div>
      <div className={styles.buttons}>
        <Button variant="secondary" onClick={() => onBack()}>
          Back
        </Button>
        <Button
          onClick={() => {
            handleSave();
            onBack();
          }}
        >
          Confirm
        </Button>
      </div>
    </div>
  );
};

export default OrderTimeSelector;
